// ---------------------------------------------------------------------------
// CALL-SITE integration layer for game events.
//
// This is the ONLY place where game vocabulary (win / cashout / crash / bet)
// meets the otherwise game-agnostic toast system. It translates a game event
// into a notify() call with the right title, copy, and sound.
//
// Wire these into the real round/bet/socket handlers as they are built. The
// repo does not have a gameplay event layer yet, so nothing calls most of
// these today — they are ready to drop in.
//
// IMPORTANT: pass already-formatted currency strings (e.g. '+₦545.00'). Format
// at the true call site with the app's formatter (renderValue / useRenderValue
// + wallet displayCurrencyCode); never format inside the toast.
//
// SOUND: every type already maps to a default app sound in toastConstants.js
// (success->WON, error->LOST, warning/info->CLICK, loading->silent). Only pass
// `sound` to OVERRIDE that default — e.g. cashout overrides to CLICK so it is
// audibly distinct from a win (both are `success`). Keys reference the app's
// existing sound manager keys (src/core/audio/sounds/soundMap.js: WON|LOST|CLICK).
// ---------------------------------------------------------------------------
import { notify } from './notify'

export const gameToasts = {
  // Player wins a settled round. (`success` already defaults to WON.)
  win: ({ amount, multiplier } = {}) =>
    notify('success', { title: 'You won', amount, meta: multiplier }),

  // Player cashes out — also a success, but override to a distinct cue. Pass
  // `onShare` (only when chat is enabled) to surface a "Share" button; the win
  // is shared to chat ONLY when the player taps it. Shareable toasts linger a
  // little longer so there's time to press.
  cashout: ({ amount, multiplier, onShare } = {}) =>
    notify('success', {
      title: 'Cashed out',
      amount,
      meta: multiplier,
      sound: 'CLICK',
      ...(onShare
        ? { action: { label: 'Share', onClick: onShare }, duration: 8000 }
        : {}),
    }),

  // Round crashes / player loses. (`error` already defaults to LOST.)
  roundLost: ({ amount, multiplier } = {}) =>
    notify('error', {
      title: 'Round lost',
      amount,
      sub: multiplier ? `Crashed at ${multiplier}` : undefined,
    }),

  // Bet accepted by the server (default success sound).
  betAccepted: ({ stake } = {}) => notify('success', { sub: `${stake} on next round` }),

  // Bet submitted, awaiting server ack. Returns the toast id; resolve it later
  // with betPlaced({ betId }). 60s safety duration in case the ack is lost.
  betSubmitting: ({ betId, stake } = {}) =>
    notify('loading', {
      id: betId,
      sub: stake ? `Placing ${stake}…` : 'Placing bet…',
      duration: 60000,
    }),

  // Server acked the bet — resolves the loading toast in place (same id).
  betPlaced: ({ betId } = {}) => notify('success', { id: betId, title: 'Bet placed' }),

  // Balance too low to place a bet.
  lowBalance: () => notify('warning', { sub: 'Top up to keep playing' }),

  // Generic request / socket error.
  requestError: (message) => notify('error', { sub: message }),

  // Informational round notices (e.g. "Next round boarding").
  info: ({ title, sub } = {}) => notify('info', { title, sub }),
}
